import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';

/* Compact globe + chevron with dropdown of supported languages.
 * Used in the public site header (desktop + mobile menu).
 * Avoids translating its own labels — language names are always shown in their
 * native form so users can self-identify regardless of current site language.
 */
const LanguageSelector = ({ variant = 'desktop' }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];

  if (variant === 'mobile') {
    // Stacked, full-width buttons for the mobile menu
    return (
      <div className="border-t border-[#E5E7EB] pt-3 mt-3" data-testid="lang-selector-mobile">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-2 inline-flex items-center gap-1">
          <Globe className="w-3 h-3" /> Language
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button key={l.code} type="button"
              onClick={() => setLang(l.code)}
              className={`flex items-center gap-2 px-2.5 py-2 rounded text-xs font-semibold border ${
                lang === l.code
                  ? 'bg-[#FFF8F8] border-[#BA1826] text-[#BA1826]'
                  : 'bg-white border-[#E5E7EB] text-[#0A0A0A] hover:bg-[#F8F9FA]'
              }`}
              data-testid={`lang-option-${l.code}`}>
              <span className="text-base leading-none">{l.flag}</span>
              <span className="flex-1 text-left">{l.native}</span>
              {lang === l.code && <Check className="w-3 h-3" />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative inline-block" data-testid="lang-selector">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-[#0A0A0A] hover:bg-[#F8F9FA] rounded border border-transparent hover:border-[#E5E7EB]"
        aria-haspopup="listbox" aria-expanded={open}
        data-testid="lang-selector-toggle">
        <Globe className="w-4 h-4 text-[#4B5563]" />
        <span className="text-base leading-none">{current.flag}</span>
        <span className="hidden lg:inline">{current.native}</span>
        <ChevronDown className={`w-3 h-3 text-[#9CA3AF] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-50 py-1" role="listbox">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button key={l.code} type="button"
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-[#F8F9FA] ${
                lang === l.code ? 'bg-[#FFF8F8] text-[#BA1826] font-bold' : 'text-[#0A0A0A]'
              }`}
              data-testid={`lang-option-${l.code}`}>
              <span className="text-base leading-none">{l.flag}</span>
              <span className="flex-1">{l.native}</span>
              {lang === l.code && <Check className="w-3 h-3" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
